import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { applyMeta, type MetaInput } from '../lib/seo.js';

/**
 * Route-level SEO defaults. Mounted as the FIRST sibling in <App/> so its
 * effect runs before any page-level useMeta on navigation, pages that set
 * richer meta (footer pages, landing, detail pages with fetched data) simply
 * overwrite these afterwards.
 *
 * The landing page and footer pages manage their own tags and are not listed.
 *
 * The server sends the same titles before any JavaScript runs (API module
 * render/static-pages.ts); when a title or description changes here, change
 * it there too.
 */
type Entry = { pattern: string } & MetaInput;

const ROUTES: Entry[] = [
  {
    pattern: '/today',
    title: "Today's Prayers, Saints & Readings",
    description:
      "The Orthodox day at a glance, today's feast, fasting rule, appointed scripture readings, and the saints commemorated on the Church calendar.",
  },
  {
    pattern: '/prayers',
    title: 'Orthodox Prayer Book with Audio',
    description:
      'Morning and evening prayers, prayers for every need, and the complete Jordanville prayer book, each prayer with reverent audio to pray along.',
  },
  {
    pattern: '/prayers/jesus',
    title: 'The Jesus Prayer',
    description:
      'Pray the Jesus Prayer with a counter and breath pacing, in the tradition handed down by the Church.',
  },
  {
    pattern: '/prayers/:slug',
    title: 'Prayers',
    description: 'Orthodox prayers with full text and audio to pray along.',
  },
  {
    pattern: '/fasting',
    title: 'Orthodox Fasting Calendar',
    description:
      'What the Church appoints for fasting today and through the year, the great fasts, fast-free weeks, and simple guidance for keeping them.',
  },
  {
    pattern: '/readings',
    title: 'Daily Scripture Readings',
    description:
      'The appointed epistle and gospel readings for every day of the Orthodox liturgical year, with the full text and audio.',
  },
  {
    // The page sets the exact date in its own title once it renders.
    pattern: '/readings/:date',
    title: 'Orthodox Daily Readings',
    description:
      'The epistle and gospel appointed for this day of the Orthodox liturgical year, with the full text and audio.',
  },
  {
    pattern: '/reading-plans',
    title: 'Bible Reading Plans',
    description:
      'Read through the Gospels, the Psalms, and the New Testament with steady daily plans that keep your place.',
  },
  {
    pattern: '/services',
    title: 'Divine Services • Texts & Audio',
    description:
      'Follow the Divine Liturgy, Vespers, Matins, Compline, and other services with complete texts and sung audio to pray along.',
  },
  {
    pattern: '/services/:slug',
    title: 'Divine Services',
    description: 'Orthodox service texts with the appointed readings and hymns for the day.',
  },
  {
    pattern: '/learning',
    title: 'Learn the Orthodox Faith',
    description:
      'Articles on Orthodox doctrine, worship, spirituality, and Church history, a library for inquirers, catechumens, and the faithful.',
  },
  {
    pattern: '/learn/category/:slug',
    title: 'Learn the Orthodox Faith',
    description: 'Articles on the Orthodox faith, gathered by theme.',
  },
  {
    pattern: '/learn/articles/:slug',
    title: 'Learn the Orthodox Faith',
    description: 'An article from the Orthodox Way library on the faith and life of the Church.',
  },
  {
    pattern: '/resources',
    title: 'Scripture & Spiritual Reading',
    description:
      'Read the New Testament, the Psalter, and early Christian writings in a quiet, distraction-free reader.',
  },
  {
    pattern: '/resources/:work',
    title: 'Scripture & Spiritual Reading',
    description: 'Scripture and spiritual works to read slowly and without distraction.',
  },
  {
    pattern: '/resources/:work/:book',
    title: 'Scripture & Spiritual Reading',
    description: 'Scripture and spiritual works to read slowly and without distraction.',
  },
  {
    pattern: '/resources/:work/:book/:chapter',
    title: 'Scripture & Spiritual Reading',
    description: 'Scripture and spiritual works to read slowly and without distraction.',
  },
  {
    pattern: '/journey',
    title: "The Catechumen's Journey",
    description:
      'A guided path for inquirers and catechumens, from first questions to life in the Church, with a priest to ask along the way.',
  },
  {
    pattern: '/watch',
    title: 'Watch • Orthodox Teaching & Films',
    description:
      'Video teaching on the Orthodox faith, homilies, catechesis, and the lives of the saints.',
  },
  {
    pattern: '/calendar',
    title: 'Orthodox Liturgical Calendar',
    description:
      "The Church's calendar of feasts, fasts, and commemorations for every day of the year, on the old and new calendar.",
  },
  {
    pattern: '/feasts',
    title: 'Great Feasts of the Church',
    description:
      'Pascha and the twelve Great Feasts, their meaning, hymns, icons, and their dates for this year.',
  },
  {
    pattern: '/feasts/:slug',
    title: 'Great Feasts of the Church',
    description: 'A Great Feast of the Orthodox Church, its meaning, hymns, and icon.',
  },
  {
    pattern: '/saints',
    title: 'Lives of the Saints',
    description:
      'The lives of the saints commemorated through the year, biographies, icons, troparia, and feast days.',
  },
  {
    pattern: '/saints/:slug',
    title: 'Lives of the Saints',
    description:
      'The life of a saint of the Orthodox Church, biography, icon, and commemoration days.',
  },
  {
    pattern: '/icons',
    title: 'Holy Icons',
    description: 'A gallery of holy icons to venerate, with the story each icon carries.',
  },
  {
    pattern: '/parish',
    title: 'Find an Orthodox Parish',
    description:
      'Find an Orthodox parish near you, service times, bulletins, and community announcements.',
  },
  {
    pattern: '/parishes/:slug/bulletin',
    title: 'Parish Bulletin',
    description: 'This week at the parish, services, announcements, and commemorations.',
  },
  // Private / utility routes - crawlers should not index these.
  { pattern: '/login', title: 'Sign in', description: 'Sign in to Orthodox Way.', noindex: true },
  {
    pattern: '/register-parish',
    title: 'Register your parish',
    description: 'Register an Orthodox parish on Orthodox Way.',
    noindex: true,
  },
  {
    pattern: '/profile',
    title: 'Profile',
    description: 'Your Orthodox Way profile.',
    noindex: true,
  },
  { pattern: '/tracking', title: 'Prayer life', description: 'Your prayer life.', noindex: true },
  {
    pattern: '/commemorations',
    title: 'Commemoration book',
    description: 'Your commemoration book.',
    noindex: true,
  },
  {
    pattern: '/confession',
    title: 'Confession preparation',
    description: 'Preparation for confession.',
    noindex: true,
  },
  { pattern: '/admin/*', title: 'Admin', description: 'Administration.', noindex: true },
  { pattern: '/admin', title: 'Admin', description: 'Administration.', noindex: true },
];

export function RouteSeo(): null {
  const { pathname } = useLocation();

  useEffect(() => {
    const entry = ROUTES.find((r) => matchPath(r.pattern, pathname));
    if (entry) {
      applyMeta({ ...entry, path: pathname });
    }
  }, [pathname]);

  return null;
}
